import { defineStore } from "pinia";
import { ref, computed } from "vue";
import { gafService } from "@/services/gafService";
import customerService from "@/services/customerService";
import { useMeasurementStore } from "@/stores/measurementStore";
import { useUserStore } from '@/stores/userStore'

export const useGafStore = defineStore("gaf", () => {
  const isConfigured = ref(false);
  const error = ref(null);
  const loading = ref(false);

  const accountExists = ref(false);
  const accountEmail = ref('');
  const availableCredits = ref(0);
  const propertyTypes = ref([]);
  const products = ref([]);
  const searchTerm = ref("");
  const orderNumber = ref("");
  const itemsPerPage = ref(10);
  const currentPage = ref(1);
  const isInitializing = ref(false);
  const totalPages = ref(0);

  const connectButtonClicked = ref(false);
  
  const reports = ref({
    ReportList: [],
  });
  
  const totalRecords = ref(0);
  const isLoadingMore = ref(false);
  
  const userStore = useUserStore();
  const currentUserEmail = computed(() => userStore.userDetails?.Email)
  
  const getDemoStatus = (email) => {
    const cleanEmail = (email || "").toLowerCase().trim();

    if (cleanEmail === "demo001" || cleanEmail === "demo@srsdistribution.com") {
      localStorage.setItem("isConnectedDEMO001", "true");
    }

    return localStorage.getItem("isConnectedDEMO001") === "true";
  };

  const connectToGaf = async () => {
    isInitializing.value = true;
    loading.value = true;
    error.value = null;

    try {
      if (!currentUserEmail.value) {
        await userStore.getUserDetails()
      }

      await getGafAccountInfo();
      const payload = { SystemIdentifier: "GAF", UserSpecific: true };
      const res = await customerService.getSystemIntegrationHasIntegrationConfigured(payload);

      isConfigured.value = res.data;

      if (isConfigured.value) {
        currentPage.value = 1;
        await searchReports({resetPage: true, onlyMeasurements: true});
      }
    } catch (err) {
      error.value = err.message;
    } finally {
      loading.value = false;
      isInitializing.value = false;
    }
  };

  const getGafAccountInfo = async () => {
    loading.value = true;
    try {
      if (!currentUserEmail.value) {
        await userStore.getUserDetails()
      }
      const res = await gafService.getAccount(currentUserEmail.value);

      if (res.success && res.data?.Success) {
        accountExists.value = true;
        accountEmail.value = res.data.EmailAddress || currentUserEmail.value || '';
        availableCredits.value = res.data.AvailableCredits || 0;
        propertyTypes.value = res.data.PropertyTypes || [];
        products.value = res.data.Products || [];
      } else {
        accountExists.value = false;
        accountEmail.value = '';
      }
    } catch (err) {
      error.value = err.message || "Failed to get GAF account info.";
      accountEmail.value = '';
    } finally {
      loading.value = false;
    }
  };

  const connectAccount = async () => {
    loading.value = true;

    try {
      if (!currentUserEmail.value) {
        await userStore.getUserDetails();
      }

      const isDemo = getDemoStatus(currentUserEmail.value);

      const payload = {
        SystemIdentifier: "GAF",
        IsUserSpecific: true,
        IsConnectedDEMO001: isDemo,
        Password: "",
        Username: currentUserEmail.value,
      };

      await gafService.connectIntegration(payload);

      currentPage.value = 1;

  await searchReports({ onlyMeasurements: true });
    } catch (err) {
      error.value = err;
    } finally {
      loading.value = false;
    }
  };

  const searchReports = async ({ resetPage = true, onlyMeasurements = false } = {}) => {
    loading.value = true;
    error.value = null;

    try {
      if (!currentUserEmail.value) {
        await userStore.getUserDetails();
      }

      if (resetPage) {
        currentPage.value = 1;
      }

      const res = await gafService.searchReports({
        EmailAddress: currentUserEmail.value,
        OrderNumber: orderNumber.value,
        SearchTerm: searchTerm.value,
        NumberOfItemsToRetrieve: itemsPerPage.value,
        PageNumber: currentPage.value,
        OnlyMeasurements: onlyMeasurements,
      });

      if (res?.Success) {
        reports.value.ReportList = res.Result;
        totalRecords.value = res.Paging?.TotalRecords ?? 0;
        totalPages.value = res.Paging?.TotalPages ?? 0;
      } else {
        reports.value.ReportList = [];
        totalRecords.value = 0;
      }
    } catch (err) {
      error.value = err.message || "Failed to search reports.";
    } finally {
      loading.value = false;
    }
  };

  const loadMoreErrorCount = ref(0);

  const loadMoreReports = async (itemsToRetrieve = 2) => {
    if (isLoadingMore.value) return;
    if (currentPage.value >= totalPages.value) return;

    isLoadingMore.value = true;
    error.value = null;

    try {
      /**
       * First time it is fetching 10 items in page 1.
       * From page 2 onwards we are fetching only 2 items per page.
       * So we need to skip first 4 pages to avoid duplicate items.
       */ 
      if ( currentPage.value === 1 ) {
        currentPage.value = 5;
      }
      currentPage.value += 1;
      const numberOfItemsToRetrieve = itemsToRetrieve;

      const res = await gafService.searchReports({
        EmailAddress: currentUserEmail.value,
        OrderNumber: orderNumber.value,
        SearchTerm: searchTerm.value,
        NumberOfItemsToRetrieve: numberOfItemsToRetrieve,
        PageNumber: currentPage.value,
        OnlyMeasurements: false,
      });

      if (res?.Success) {
        reports.value.ReportList = [...reports.value.ReportList, ...res.Result];
        totalPages.value = res.Paging?.TotalPages ?? 0;
        loadMoreErrorCount.value = 0;
      }
    } catch (err) {
      if ( loadMoreErrorCount.value >= 3 ) {
        error.value = err.message || "Failed to load more reports.";
      } else {
        console.warn("Load more reports failed, retrying...", err);
        loadMoreErrorCount.value += 1;
        isLoadingMore.value = false;
        loadMoreReports();
      }
    } finally {
      isLoadingMore.value = false;
    }
  };

  const downloadPdf = (reportId) => {
    window.open(`/gaf/downloadreport/${reportId}`, "_blank");
  };

  const importReport = async (report) => {
    const measurementStore = useMeasurementStore();
    loading.value = true;
    error.value = null;

    try {
      const meta = report.reportMetaData || {};

      const mappedData = {
        TotalArea: meta.Area || 0,
        Ridges: meta.Ridges || 0,
        Valleys: meta.Valleys || 0,
        Eaves: meta.Eaves || 0,
        Rakes: meta.Rakes || 0,
        Hips: meta.Hips || 0,
        Flashing: meta.Flash || 0,
        StepFlashing: meta.Step || 0,
      };

      const metaData = {
        provider: "GAF",
        id: report.orderId,
        link: `/gaf/downloadreport/${report.orderId}`,
      };
      measurementStore.setImportedMeasurementData(mappedData, metaData);
    } catch (err) {
      error.value = err.message;
    } finally {
      loading.value = false;
    }
  };

  const clearReports = () => {
    reports.value = { ReportList: [] };
    totalRecords.value = 0;
    currentPage.value = 1;
    totalPages.value = 0;
    searchTerm.value = "";
    orderNumber.value = "";
    error.value = null;
  };

  return {
    isConfigured,
    currentUserEmail,
    loading,
    error,
    accountExists,
    accountEmail,
    availableCredits,
    propertyTypes,
    products,
    connectButtonClicked,
    reports,
    searchTerm,
    itemsPerPage,
    currentPage,
    totalRecords,
    isLoadingMore,
    orderNumber,
    isInitializing,
    totalPages,

    connectToGaf,
    getGafAccountInfo,
    connectAccount,
    searchReports,
    fetchReports: searchReports,
    loadMoreReports,
    downloadPdf,
    importReport,
    clearReports,
  };
});
